import { Link } from "react-router-dom";
import { ArrowRight, Globe2, HandHeart, Leaf, Mail, MessageCircle, Sparkles, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { InquiryForm } from "../components/Forms";
import { artisanStories, founderQuote, giftingUseCases, sustainabilityPoints, whyBuyPoints } from "../data/siteContent";

export function AboutPage() {
  return (
    <StoryShell kicker="About Project Umeed" title="An international craft label in the making, rooted in Kutch." testId="about-page">
      <p data-testid="about-page-copy-one">Project Umeed creates long-term livelihood opportunities for women artisans in Kutch by bringing premium handcrafted textile products to conscious wardrobes, modern homes, gifting occasions and global markets.</p>
      <p data-testid="about-page-copy-two">The brand is not built on sympathy. It is built on skill, cultural identity, design direction, storytelling, digital visibility and access to buyers who value handmade craft.</p>
      <div className="value-grid" data-testid="about-values-grid">{whyBuyPoints.slice(0, 8).map((item, index) => <ValuePill key={item} icon={<HandHeart />} text={item} id={`about-value-${index}`} />)}</div>
    </StoryShell>
  );
}

export function ArtisansPage() {
  return (
    <StoryShell kicker="Meet the Artisans" title="Creators, makers and cultural carriers." testId="artisans-page">
      <p data-testid="artisans-page-copy">The artisans behind Project Umeed are not passive beneficiaries. They are women creators whose textile skill carries identity, memory and future possibility.</p>
      <div className="artisan-grid artisan-grid--wide" data-testid="artisans-page-grid">
        {artisanStories.map((artisan) => (
          <article className="artisan-card" key={artisan.name} data-testid={`artisans-page-card-${artisan.name.toLowerCase().replaceAll(" ", "-")}`}>
            <Users aria-hidden="true" />
            <h3 data-testid={`artisans-page-name-${artisan.name.toLowerCase().replaceAll(" ", "-")}`}>{artisan.name}</h3>
            <p data-testid={`artisans-page-skill-${artisan.name.toLowerCase().replaceAll(" ", "-")}`}>{artisan.skill}</p>
            <span data-testid={`artisans-page-contribution-${artisan.name.toLowerCase().replaceAll(" ", "-")}`}>{artisan.contribution}</span>
            <p data-testid={`artisans-page-story-${artisan.name.toLowerCase().replaceAll(" ", "-")}`}>{artisan.story}</p>
            <strong data-testid={`artisans-page-access-${artisan.name.toLowerCase().replaceAll(" ", "-")}`}>{artisan.access}</strong>
          </article>
        ))}
      </div>
    </StoryShell>
  );
}

export function SustainabilityPage() {
  return (
    <StoryShell kicker="Sustainability" title="Sustainability That Feels Personal" testId="sustainability-page">
      <p data-testid="sustainability-page-copy">Project Umeed believes sustainability is not just about materials. It is also about people, livelihoods, culture, and longevity. Every product is designed to last longer, carry meaning, and reduce the distance between maker and buyer.</p>
      <div className="value-grid" data-testid="sustainability-points-grid">{sustainabilityPoints.map((item, index) => <ValuePill key={item} icon={<Leaf />} text={item} id={`sustainability-point-${index}`} />)}</div>
    </StoryShell>
  );
}

export function ImpactPage({ impact }) {
  return (
    <StoryShell kicker="Impact" title="Impact You Can Carry" testId="impact-page">
      <p data-testid="impact-page-copy">Every product helps create visibility, income opportunity and long-term dignity for women artisans.</p>
      <div className="impact-metrics impact-metrics--page" data-testid="impact-page-metrics">
        {impact.metrics?.map((metric) => (
          <div key={metric.label} data-testid={`impact-page-metric-${metric.label.toLowerCase().replaceAll(" ", "-")}`}>
            <strong>{metric.value}</strong>
            <span>{metric.label}</span>
          </div>
        ))}
      </div>
      <InquiryForm type="support" title="Support the Movement" description="Share how you would like to buy, follow, donate, collaborate or create access." testPrefix="support" />
    </StoryShell>
  );
}

export function GiftingPage() {
  return (
    <StoryShell kicker="Corporate & Conscious Gifting" title="Gifts That Carry Meaning" testId="gifting-page">
      <p data-testid="gifting-page-copy">For companies and individuals who want their gifts to feel premium, personal, ethical, and memorable, Project Umeed offers handcrafted textile products that carry both beauty and impact.</p>
      <div className="value-grid" data-testid="gifting-use-cases-grid">{giftingUseCases.map((item, index) => <ValuePill key={item} icon={<Sparkles />} text={item} id={`gifting-use-case-${index}`} />)}</div>
      <InquiryForm type="gifting" title="Enquire for Gifting" description="Tell us the occasion, quantity, timeline and products you are considering." testPrefix="gifting" />
    </StoryShell>
  );
}

export function FounderPage() {
  return (
    <StoryShell kicker="Founder’s Note" title="A Note from Sara" testId="founder-page">
      <blockquote className="large-quote" data-testid="founder-page-quote">“{founderQuote}”</blockquote>
      <p data-testid="founder-page-copy">Sara supports artisans through design direction, product development, photography, Instagram visibility, sales support, logistics, packaging, storytelling, customer access, pricing guidance and digital market reach.</p>
      <Button className="btn-primary" asChild data-testid="founder-page-contact-link"><Link to="/contact">Connect with Project Umeed <ArrowRight aria-hidden="true" /></Link></Button>
    </StoryShell>
  );
}

export function CollaboratePage() {
  return (
    <StoryShell kicker="Collaborate" title="Partner with women-led craft from Kutch." testId="collaborate-page">
      <p data-testid="collaborate-page-copy">Project Umeed welcomes boutique buyers, conscious retailers, sustainable brands, gifting partners, creative collaborators and impact-led organisations.</p>
      <div className="value-grid" data-testid="collaborate-options-grid">
        {[
          "Partner as retailer or boutique",
          "Create a gifting collection",
          "Build a sustainable brand collaboration",
          "Support digital visibility",
          "Commission textile-led products",
          "Bring Kutch craft to international audiences",
        ].map((item, index) => <ValuePill key={item} icon={<Globe2 />} text={item} id={`collaborate-option-${index}`} />)}
      </div>
      <InquiryForm type="collaboration" title="Collaborate With Us" description="Share your idea, audience and timeline." testPrefix="collaborate" />
    </StoryShell>
  );
}

export function ContactPage({ contact }) {
  const cleanNumber = contact.whatsapp_number.replace(/\D/g, "") || "910000000000";
  return (
    <StoryShell kicker="Contact" title="Start with a product, a gift, or a partnership." testId="contact-page">
      <div className="contact-actions" data-testid="contact-actions">
        <a href={`https://wa.me/${cleanNumber}`} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-link"><MessageCircle aria-hidden="true" /> {contact.whatsapp_number}</a>
        <a href={`mailto:${contact.email}`} data-testid="contact-email-link"><Mail aria-hidden="true" /> {contact.email}</a>
      </div>
      <InquiryForm type="contact" title="Write to Project Umeed" description="For product orders, gifting, collaborations or support." testPrefix="contact" />
    </StoryShell>
  );
}

function StoryShell({ kicker, title, children, testId }) {
  return (
    <section className="story-page section-pad" data-testid={testId}>
      <div className="page-intro page-intro--story">
        <p className="section-kicker" data-testid={`${testId}-kicker`}>{kicker}</p>
        <h1 data-testid={`${testId}-heading`}>{title}</h1>
      </div>
      <div className="story-page__content" data-testid={`${testId}-content`}>{children}</div>
    </section>
  );
}

function ValuePill({ icon, text, id }) {
  return (
    <div className="value-pill" data-testid={id}>
      {icon}
      <span>{text}</span>
    </div>
  );
}